import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

const ICONS = ["Activity", "BarChart3", "Bell", "Cpu"];

export default function AdminHomeModulesPage() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ icon: "Activity", title: "", desc: "", tag: "", order: 0, isPublished: true });

  async function load() {
    setError("");
    const res = await adminService.listHomeModules();
    if (!res.ok) return setError(res.error || "Failed to load home modules");
    setItems(res.data?.items || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Home Modules" highlight="content." description="Manage the public home feature modules." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? (
            <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card>
          ) : null}

          <div>
            <Button onClick={() => { setDraft({ icon: "Activity", title: "", desc: "", tag: "", order: 0, isPublished: true }); setAddOpen(true); }}>
              Add Module
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {items.map((m) => (
              <Card key={m._id} className="shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-4">
                    <span>{m.title}</span>
                    <span className="text-xs text-muted-foreground">{m.tag}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-3">
                  <div className="text-xs">icon: {m.icon}</div>
                  <div>{m.desc}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(m); setDraft({ icon: m.icon, title: m.title, desc: m.desc, tag: m.tag, order: m.order || 0, isPublished: m.isPublished !== false }); setEditOpen(true); }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={async () => {
                      const r = await adminService.deleteHomeModule(m._id);
                      if (!r.ok) return toast.error(r.error || "Delete failed");
                      toast.success("Module deleted");
                      await load();
                    }}>
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageSection>

      <FormModal open={addOpen} title="Add Home Module" onClose={() => setAddOpen(false)}>
        <div className="grid gap-3">
          <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.icon} onChange={(e) => setDraft((p) => ({ ...p, icon: e.target.value }))}>
            {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={draft.title} onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Tag (e.g. Live)" value={draft.tag} onChange={(e) => setDraft((p) => ({ ...p, tag: e.target.value }))} />
          <textarea className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const r = await adminService.createHomeModule(draft);
              if (!r.ok) return toast.error(r.error || "Create failed");
              toast.success("Module created");
              setAddOpen(false);
              await load();
            }}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={editOpen} title="Edit Home Module" onClose={() => setEditOpen(false)}>
        <div className="grid gap-3">
          <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.icon} onChange={(e) => setDraft((p) => ({ ...p, icon: e.target.value }))}>
            {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={draft.title} onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))} />
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Tag" value={draft.tag} onChange={(e) => setDraft((p) => ({ ...p, tag: e.target.value }))} />
          <textarea className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              if (!editing?._id) return;
              const r = await adminService.updateHomeModule(editing._id, draft);
              if (!r.ok) return toast.error(r.error || "Update failed");
              toast.success("Module updated");
              setEditOpen(false);
              await load();
            }}>Update</Button>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>
    </PageShell>
  );
}

